import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Mail, Users, Shield } from "lucide-react";
import { api } from "@/lib/api";

interface SharedEmailAccount {
  user_id: number;
  user_name: string;
  user_email: string;
  email_address: string;
  type: 'manual' | 'oauth';
  provider?: string;
}

export function TeamEmailAccounts() {
  const [sharedAccounts, setSharedAccounts] = useState<SharedEmailAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSharedAccounts();
  }, []);

  const loadSharedAccounts = async () => {
    try {
      const response = await api.get('/api/players/team-shared-emails');
      if (response.data.shared_emails) {
        setSharedAccounts(response.data.shared_emails);
      }
    } catch (error) {
      console.error('Error loading shared emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProviderIcon = (provider?: string) => {
    switch (provider) {
      case 'google':
        return '🔵';
      case 'microsoft':
      case 'azure':
        return '🟦';
      default:
        return '📧';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Email Accounts
          </CardTitle>
          <CardDescription>
            Loading team member email sharing status...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Team Email Accounts
        </CardTitle>
        <CardDescription>
          Team members who are sharing their recruitment emails with you.
          Each member manages their own email fetching - shared players appear automatically in your dashboard.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {sharedAccounts.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Shared Email Accounts</h3>
            <p className="text-gray-500 mb-4">
              No team members are currently sharing their recruitment emails.
            </p>
            <p className="text-sm text-gray-400">
              Team members can enable email sharing in their team settings.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sharedAccounts.map((account) => (
              <div key={account.user_id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-blue-100 text-blue-700">
                      {account.user_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">{account.user_name}</div>
                    <div className="text-sm text-gray-500">{account.user_email}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs">{getProviderIcon(account.provider)}</span>
                      <span className="text-xs text-gray-400">{account.email_address}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    {account.type === 'oauth' ? 'OAuth' : 'Manual'}
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    Sharing Enabled
                  </Badge>
                </div>
              </div>
            ))}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="text-blue-600 mt-0.5">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">How Email Sharing Works</h4>
                  <p className="text-sm text-blue-700">
                    Each team member fetches their own recruitment emails using the regular "Refresh Emails" button. 
                    When email sharing is enabled, their discovered players automatically appear in your shared dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}